import { useState, useEffect, useRef } from 'react';

/**
 * AnimatedNumber Component
 * Smoothly animates number changes with a counting/scrolling effect
 *
 * @param {number} value - The target number to display
 * @param {function} formatter - Optional function to format the number for display
 * @param {number} duration - Animation duration in ms (default: 1000)
 * @param {string} className - Additional CSS classes
 */
export function AnimatedNumber({
  value,
  formatter = (n) => n.toLocaleString(),
  duration = 1000,
  className = '',
}) {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);
  const previousValueRef = useRef(value);
  const animationRef = useRef(null);

  useEffect(() => {
    const previousValue = previousValueRef.current;

    // Skip animation if value hasn't changed or is initial render
    if (previousValue === value) {
      return;
    }

    // Cancel any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    setIsAnimating(true);
    const startTime = performance.now();
    const startValue = previousValue;
    const endValue = value;
    const difference = endValue - startValue;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function: easeOutCubic for smooth deceleration
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      const currentValue = startValue + (difference * easeProgress);
      setDisplayValue(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(endValue);
        setIsAnimating(false);
        previousValueRef.current = endValue;
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, duration]);

  // Update ref when value changes (for cases where animation is skipped)
  useEffect(() => {
    previousValueRef.current = value;
  }, [value]);

  // Initial render - set display value without animation
  useEffect(() => {
    if (previousValueRef.current === 0 && value > 0) {
      setDisplayValue(value);
      previousValueRef.current = value;
    }
  }, []);

  return (
    <span
      className={`${className} ${isAnimating ? 'transition-transform' : ''}`}
      style={{
        display: 'inline-block',
        // Subtle scale pulse during animation
        transform: isAnimating ? 'scale(1.02)' : 'scale(1)',
        transition: 'transform 0.1s ease-out',
      }}
    >
      {formatter(Math.round(displayValue))}
    </span>
  );
}

export default AnimatedNumber;
